import {
  createObjectUpdate,
  deleteObjectUpdate,
  editObjectUpdate,
  retrieveObjectUpdates,
} from "@/lib/data/requests/updates/object-updates";
import type { ObjectUpdate } from "@/lib/data/schemas/updates/ObjectUpdateSchema";
import { queryKeys } from "@/lib/data/static/query-keys";
import type { APIError } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import {
  infiniteQueryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import toast from "react-hot-toast";

export const retrieveObjectUpdatesInfiniteQueryOptions = (
  data: Omit<Parameters<typeof retrieveObjectUpdates>[0], "cursor">,
) => {
  const params = { ...data, limit: data.limit ?? 50 };

  return infiniteQueryOptions({
    queryKey: [
      queryKeys.objectUpdates,
      data.anchorObjectType,
      data.anchorObjectId,
      params.limit,
    ],
    enabled: Boolean(data.anchorObjectId),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      retrieveObjectUpdates({ ...params, cursor: pageParam }),
    getNextPageParam: (lastPage) => lastPage.data.nextCursor ?? undefined,
  });
};

const invalidateUpdateFeeds = async (
  queryClient: QueryClient,
  update: ObjectUpdate,
) => {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: [queryKeys.objectUpdates, "matter", update.matterId],
    }),
    ...(update.anchorObjectType === "matter_expense"
      ? [
          queryClient.invalidateQueries({
            queryKey: [
              queryKeys.objectUpdates,
              "matter_expense",
              update.anchorObjectId,
            ],
          }),
        ]
      : []),
  ]);
};

export function useCreateObjectUpdate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createObjectUpdate,
    onSuccess: async (response) => {
      await invalidateUpdateFeeds(queryClient, response.data);
      toast.success("Update created successfully");
    },
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error creating update");
    },
  });
}

export function useEditObjectUpdate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: editObjectUpdate,
    onSuccess: async (response) => {
      await invalidateUpdateFeeds(queryClient, response.data);
      toast.success("Update saved successfully");
    },
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error saving update");
    },
  });
}

export function useDeleteObjectUpdate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteObjectUpdate,
    onSuccess: async (response) => {
      await invalidateUpdateFeeds(queryClient, response.data);
      toast.success("Update deleted successfully");
    },
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error deleting update");
    },
  });
}
